/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { segment } from '@actions/analytics';
import { fetchPartPaymentStatus } from '@actions/checkout';
import { getPlannerSchedule } from '@actions/courses';
import Drawer from '@base/Drawer/Drawer';
import H3 from '@base/H3/H3';
import H6 from '@base/H6/H6';
import Tags from '@base/Tags/Tags';
import AboutDrawer from '@comp/AboutDrawer/AboutDrawer';
import Dialog from '@comp/Dialog/Dialog';
import FreePlanUnlock from '@comp/FreePlanUnlock/FreePlanUnlock';
import ShareDialog from '@comp/ShareDialog/ShareDialog';
import {
  evtNameCategoryMap,
  programmeTypes,
  subscriptionTypes,
  unlockFreePlanFor
} from '@constants/index';
import {
  ADD_TO_PLANNER_SKIP_CLICKED,
  ADD_TO_PLANNER_VIEWED,
  CHECKOUT_STARTED,
  COURSE_ABOUT,
  COURSE_KEYS,
  COURSE_PREVIEW,
  TEST_SERIES_ABOUT,
  TEST_SERIES_KEYS
} from '@constants/segment';
import styled from '@emotion/styled';
import segmentStorage from '@events/segmentStorage';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import useEducatorContext from '@hooks/useEducatorContext';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useWindowSize from '@hooks/useWindowSize';
import { Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import pushRoute from '@utils/pushRoute';
import getIsSubscribedForContent from '@utils/subscriptionHelpers/getIsSubscribedForContent';
import { resolveShowBuy } from '@utils/subscriptionHelpers/resolveShowBuy';
import useUserSubscriptionType from '@hooks/useUserSubscriptionType';
import useLiteUpgradeDialog from '@hooks/useLiteUpgradeDialog';
import { getMe, getIsLoggedIn, getIsFetchingUserData } from '@selectors/auth';
import {
  isScheduledSelector,
  plannerScheduleSelector,
  isCompletedSelector,
  courseAvailableForSelector
} from '@selectors/courses';
import {
  getPlusSubscriptionInfoLatest,
  getIsSubscribedToGoal,
  getIsSubscribedToIconic,
  getIsSubscribedToIconicPro,
  getIsSubscribedToLite
} from '@selectors/subscription';
import useAccessControl from '@api/hooks/auth/useAccessControl';
import { getCheckPartPaymentStatus } from '@api/selectors/auth';
import { NEOLESSON } from '@utils/constants';
import { readCookie } from '@utils/cookies';
import useOneLinkSmartScriptV2 from '@hooks/useOneLinkSmartScriptV2';
import FreeTrial from '@cont/Batch/FreeTrial';
import useFreeTrialStore from '@cont/NewPreSubscribed/FreeTrial/freeTrialStore';
import useFreeTrial from '@cont/NewPreSubscribed/FreeTrial/useFreeTrial';
import Dynamic from '@base/Dynamic/Dynamic';
import AddCourseToPlannerCard from '../AddCourseToPlannerCard';
import AddCourseToPlannerDrawer from './AddCourseToPlanner/AddDrawer';
import AddToPlannerDialog from './AddCourseToPlanner/AddToPlannerDialog';
import AddedToPlannerCard from './AddedToPlannerCard';
import CourseBreadcrumbs from './CourseBreadcrumbs';
import LiteUpgradeDialog from './LiteUpgradeDialog';
import SubHeader from './SubHeader';
import CourseThumbnail from './CourseThumbnail';
import CourseDescription from './CourseDescription';
import CourseUpdates from './CourseUpdates';
import { getAuthorsList, getAuthorsName } from '../utils';

const ActiveSubscriptionModal = Dynamic(() =>
  import('@cont/NewPreSubscribed/FreeTrial/ActiveSubscriptionModal')
);
const FreePlanModal = Dynamic(() =>
  import('@cont/NewPreSubscribed/FreeTrial/FreePlanModal')
);

const Wrapper = styled.div`
  grid-column: span 24;
  background: var(--color-base-0);
`;

const Header = styled.div`
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_12B};
  margin-bottom: ${Spacings.SPACING_12B};
  @media (max-width: 47.95em) {
    padding: 16px 0;
    padding-bottom: 0px;
    box-shadow: none;
    background: var(--color-base-0);
    margin-bottom: ${Spacings.SPACING_10B};
  }
`;

const CourseInfo = styled.div`
  flex: 1;
  @media (max-width: 47.95em) {
    padding: 24px 0;
  }
`;

const LanguageTag = styled(Tags)`
  width: fit-content;
  transition: all 300ms ease;
  margin-bottom: ${Spacings.SPACING_2B};
`;

const CourseTitle = styled(H3)`
  width: 73%;
  color: var(--color-text-primary);
  @media (max-width: 47.95em) {
    width: 100%;
    font-size: 18px;
    line-height: 150%;
  }
`;

const HeaderContentLeft = styled.div`
  width: calc(100% - 240px);
  @media (max-width: 47.95em) {
    width: 100%;
    margin-top: 8px;
  }
`;

const Flex = styled.div`
  display: flex;
  overflow: visible;
  justify-content: space-between;
  @media (max-width: 47.95em) {
    flex-direction: column;
  }
`;

const EducatorName = styled(H6)`
  cursor: pointer;
  color: var(--color-text-primary);
  margin-top: ${Spacings.SPACING_6B};
  @media (max-width: 47.95em) {
    font-size: 14px;
    margin-top: 12px;
  }
`;

const PlusCoursePageHeader = ({
  course: plusCourse,
  toggleEnroll,
  handleRemoveFromPlanner,
  isTestSeries,
  setDiscussionForumDrawer,
  dataAnalytics,
  lessonList,
  isLoading,
  isRecordedGoal,
  hideCTA,
  hidePlannerAndUpdates,
  hasAccessToCourse,
  freeTrialData
}) => {
  const { uid: courseUid, goalUid, isEnrolled } = plusCourse || {};
  const isNeoLesson = !!lessonList?.results?.find(
    (lesson) => lesson.classType === NEOLESSON
  );
  const {
    showLiteUpgradeDialog,
    closeLiteUpgradeDialog,
    openLiteUpgradeDialog
  } = useLiteUpgradeDialog();

  const dispatch = useDispatch();

  const isLoggedIn = useSelector(getIsLoggedIn);
  const { accessControl } = useAccessControl();
  const checkPartPaymentStatus = getCheckPartPaymentStatus(accessControl);

  const { isMobile } = useWindowSize();
  const fetchingUserData = useSelector(getIsFetchingUserData);

  const [showLoadingButton, setShowLoadingButton] = useState(true);

  useEffect(() => {
    setShowLoadingButton(
      !!(
        fetchingUserData ||
        isLoading ||
        (readCookie('accessToken') && !isLoggedIn)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchingUserData, isLoading, isLoggedIn]);
  useEffect(() => {
    if (isLoggedIn && checkPartPaymentStatus) {
      dispatch(fetchPartPaymentStatus(goalUid));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalUid, isLoggedIn, checkPartPaymentStatus, courseUid]);

  useEffect(() => {
    dispatch(getPlannerSchedule(courseUid));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEducatorSide = useEducatorContext();
  const me = useSelector(getMe);

  const hasSubscription = useSelector(getPlusSubscriptionInfoLatest(goalUid));

  // eslint-disable-next-line no-nested-ternary
  const purchaseType = hasSubscription
    ? hasSubscription.time_remaining
      ? 'extension'
      : 'renewal'
    : 'new';
  const subscribedNow = useRef(false);
  const [drawerState, setdrawerState] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [addToPlannerDrawerEditMode, setAddToPlannerDrawerEditMode] =
    useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showPlannerEditTooltip, setShowPlannerEditTooltip] = useState(false);
  const [freePlanUnlockDrawerState, setFreePlanUnlockDrawerState] =
    useState(false);

  const isIconicUser = useSelector(getIsSubscribedToIconic(goalUid));
  const isIconicProUser = useSelector(getIsSubscribedToIconicPro(goalUid));
  const isPlusUser = useSelector(getIsSubscribedToGoal(goalUid));
  const isLiteUser = useSelector(getIsSubscribedToLite(goalUid));
  const isScheduled = useSelector(isScheduledSelector(plusCourse?.uid));
  const plannerSchedule = useSelector(plannerScheduleSelector(plusCourse.uid));
  const isCompleted = useSelector(
    isCompletedSelector(plusCourse.uid, plusCourse.isCompleted)
  );
  // available_for is missing from the useCourseInfo endpoint; sourced from
  // the planner-schedule fetch instead (see courseAvailableForSelector).
  const availableFor = useSelector(courseAvailableForSelector(plusCourse?.uid));
  const userSubscriptionType = useUserSubscriptionType(goalUid);
  const goalSegmentData = useGoalEventProps({ goalUID: goalUid });
  const segmentData = useAllTypesOfCourseEventProps({ course: plusCourse });
  const generatedLink = useOneLinkSmartScriptV2({
    campaign: isTestSeries ? 'tests page' : 'course page'
  });
  const { hasFreeTrialSubscription } = useFreeTrial(goalUid);
  const isActivateFreeTrialClicked = useFreeTrialStore(
    (storeState) => storeState.buttonClicked
  );
  const showFreeTrialNudge =
    isLoggedIn &&
    isActivateFreeTrialClicked &&
    hasFreeTrialSubscription &&
    !subscribedNow.current;
  const showSubscriptionNudge =
    isLoggedIn && isActivateFreeTrialClicked && !hasFreeTrialSubscription;
  if (!courseUid || !plusCourse) return null;

  const {
    language,
    name,
    authors,
    startsAt,
    endsAt,
    liveClassCount,
    quizCount,
    uid,
    isSpecial,
    isIconic,
    isIconicPro,
    isPartOfBatch,
    courseType,
    classCount,
    isStoreExclusive
  } = plusCourse;
  const hasAvailableForList =
    Array.isArray(availableFor) && availableFor.length > 0;
  const isHybridNonOfflineCourse =
    courseType === programmeTypes.HYBRID_NON_OFFLINE;
  const eventKeys =
    courseType === programmeTypes.TESTSERIES ? TEST_SERIES_KEYS : COURSE_KEYS;
  const isHybridCourse =
    courseType === programmeTypes.HYBRID_COURSE ||
    courseType === programmeTypes.HYBRID_TESTSERIES;
  const openAboutDrawer = () => {
    setdrawerState(true);
    const eventName =
      courseType === programmeTypes.TESTSERIES
        ? TEST_SERIES_ABOUT
        : COURSE_ABOUT;
    segment.track(
      eventName,
      { ...goalSegmentData, ...segmentData },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };
  const closeAboutDrawer = () => setdrawerState(false);

  const closeFreePlanUnlockDrawer = () => setFreePlanUnlockDrawerState(false);

  const isPlatformUnlocked = me.platformUnlockStatus?.is_platform_unlocked;

  // Store-exclusive content: hasAccessToCourse reflects real purchase
  // status (confirmed false pre-purchase, true post-purchase), and
  // resolveShowBuy additionally requires buying if the user's tier is
  // listed in available_for even without hasAccessToCourse. A FREE-tier
  // user never gets the Enroll treatment even after buying a single
  // store-exclusive course (TPE-9493) — Enroll requires an actual
  // subscription tier above FREE. Only enforce this once available_for is
  // actually configured for the course; otherwise fall back to the plain
  // tier check so unmigrated store-exclusive courses behave unchanged.
  const isSubscribed =
    isStoreExclusive && hasAvailableForList
      ? userSubscriptionType !== subscriptionTypes.FREE &&
        !resolveShowBuy(hasAccessToCourse, availableFor, userSubscriptionType)
      : getIsSubscribedForContent({
          isIconic,
          isIconicPro,
          isIconicUser,
          isIconicProUser,
          isPlusUser
        });

  const handleEnroll = async (e, bottomButton) => {
    e.stopPropagation();
    if (
      !isEducatorSide &&
      isLiteUser &&
      ((isTestSeries && isPartOfBatch) || !isTestSeries)
    ) {
      openLiteUpgradeDialog();
      return;
    }
    if (isMobile && bottomButton) {
      window.open(generatedLink, '_blank');
      return;
    }
    if (isEducatorSide) {
      setDiscussionForumDrawer(true);
      return;
    }
    if (isSubscribed || isSpecial) {
      //  if user is subscribed to goal or course is special
      if (!isPlatformUnlocked && isLoggedIn && !isSubscribed) {
        /* if user is not subscribed and is logged in and
          is platform unlocked false */
        setFreePlanUnlockDrawerState(true);
      } else {
        toggleEnroll(uid, isEnrolled, plusCourse);
        if (isCompleted && !isEnrolled && !isTestSeries && !isRecordedGoal) {
          setShowDialog(true);
          segment.track(
            ADD_TO_PLANNER_VIEWED,
            { ...goalSegmentData, ...segmentData },
            evtNameCategoryMap.WITHOUT_CATEGORY,
            true,
            eventKeys
          );
        }
      }
    } else {
      segment.track(
        CHECKOUT_STARTED,
        {
          ...goalSegmentData,
          purchase_type: purchaseType,
          last_primary_source: segmentStorage.getItem('lps'),
          is_upgrade: false
        },
        evtNameCategoryMap.BOTH
      );
      pushRoute(
        '/goal/[goalSlug]/[goalUID]/subscribe',
        `/goal/${plusCourse.goal.slug}/${plusCourse.goal.uid}/subscribe`,
        {},
        'course'
      );
    }
  };

  const upgradeSubscription = (e) => {
    e.stopPropagation();
    pushRoute(
      '/goal/[goalSlug]/[goalUID]/subscribe',
      `/goal/${plusCourse.goal.slug}/${plusCourse.goal.uid}/subscribe`,
      {},
      'course'
    );
  };

  const showIntro = () => {
    setShowPreview(true);
    segment.track(
      COURSE_PREVIEW,
      {
        ...goalSegmentData,
        ...segmentData,
        course_state: plusCourse.state
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };

  const closeShareOptiopns = () => setShowShareOptions(false);

  const handleEducator = () => {
    const filteredAuthors = getAuthorsList(authors);
    if (filteredAuthors.length > 1) openAboutDrawer(true);
    else if (filteredAuthors.length === 1) {
      pushRoute('/[authorID]', `/@${filteredAuthors[0].username}`);
    }
  };

  const renderLiteUpgradeDialog = () => {
    if (isTestSeries && isPartOfBatch) {
      return (
        <LiteUpgradeDialog
          onClose={closeLiteUpgradeDialog}
          upgradeSubscription={upgradeSubscription}
          message="Plus & Iconic learners can enroll into this test series. Also get access to notes and more self-study features."
        />
      );
    }

    return (
      <LiteUpgradeDialog
        onClose={closeLiteUpgradeDialog}
        upgradeSubscription={upgradeSubscription}
      />
    );
  };

  const onLaterClick = () => {
    setShowDialog(false);
    segment.track(
      ADD_TO_PLANNER_SKIP_CLICKED,
      { ...goalSegmentData, ...segmentData },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };

  const closeDrawer = () => {
    setShowDialog(false);
    setIsDrawerVisible(false);
  };
  const openDrawer = (eventName) => {
    setIsDrawerVisible(true);
    segment.track(
      eventName,
      { ...goalSegmentData, ...segmentData },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
    if (showDialog) setShowDialog(false);
  };

  const handleCreateCoursePlan = (eventName) => {
    setAddToPlannerDrawerEditMode(false);
    setShowPlannerEditTooltip(true);
    openDrawer(eventName);
  };

  const hidePlannerEditTooltip = () => setShowPlannerEditTooltip(false);

  const handleEditCoursePlan = (eventName) => {
    setAddToPlannerDrawerEditMode(true);
    openDrawer(eventName);
  };

  return (
    <Wrapper>
      <SubHeader
        course={plusCourse}
        isTestSeries={isTestSeries}
        dataAnalytics={dataAnalytics}
        handleEnroll={handleEnroll}
        hideCTA={hideCTA}
        hasAccessToCourse={hasAccessToCourse}
        availableFor={availableFor}
      />

      <Header>
        <div>
          {!isMobile && (
            <CourseBreadcrumbs
              isEducatorSide={isEducatorSide}
              isTestSeries={isTestSeries}
              goalData={plusCourse.goal}
              plusCourse={plusCourse}
            />
          )}
        </div>
        <Flex>
          <CourseThumbnail
            course={plusCourse}
            preview={showPreview}
            showIntro={showIntro}
            isTestSeries={isTestSeries}
            goalData={plusCourse.goal}
          />
          <CourseInfo>
            <LanguageTag type="hollow">{language}</LanguageTag>
            <Flex>
              <HeaderContentLeft>
                <CourseTitle lineClamp={0} as="h1">
                  {name}
                </CourseTitle>
                {!isTestSeries && !isEducatorSide && (
                  <EducatorName onClick={handleEducator} as="h4">
                    {getAuthorsName(authors)}
                  </EducatorName>
                )}
                <CourseDescription handleOnReadMore={openAboutDrawer}>
                  {plusCourse.description}
                </CourseDescription>
              </HeaderContentLeft>
            </Flex>
            <Drawer drawerState={drawerState} onClose={closeAboutDrawer}>
              <AboutDrawer
                data={plusCourse}
                isTestSeries={isTestSeries}
                isHybridCourse={isHybridCourse}
              />
            </Drawer>
            <Dialog onClose={closeShareOptiopns} dialogState={showShareOptions}>
              <ShareDialog data={plusCourse} useDataLink />
            </Dialog>
            <AddToPlannerDialog
              show={showDialog}
              onLaterClick={onLaterClick}
              handleCreateCoursePlan={handleCreateCoursePlan}
            />
            <AddCourseToPlannerDrawer
              lessonList={lessonList}
              isEditFlow={addToPlannerDrawerEditMode}
              drawerState={isDrawerVisible}
              closeDrawerHandler={closeDrawer}
              courseUid={courseUid}
              plannerSchedule={plannerSchedule}
              goalUid={goalUid}
              plusCourse={plusCourse}
              courseType={courseType}
            />

            {showLiteUpgradeDialog && renderLiteUpgradeDialog()}
          </CourseInfo>
        </Flex>
        <CourseUpdates
          startsAt={startsAt}
          endsAt={endsAt}
          isEnrolled={isEnrolled}
          liveClassCount={liveClassCount}
          quizCount={quizCount}
          classCount={classCount}
          openAboutDrawer={openAboutDrawer}
          isTestSeries={isTestSeries}
          isEducatorSide={isEducatorSide}
          plusCourse={plusCourse}
          me={me}
          goalSegmentData={goalSegmentData}
          segmentData={segmentData}
          eventKeys={eventKeys}
          courseType={courseType}
          lessonCount={liveClassCount}
          isNeoLesson={isNeoLesson}
          hidePlannerAndUpdates={hidePlannerAndUpdates}
          hasAccessToCourse={hasAccessToCourse}
          availableFor={availableFor}
          setDiscussionForumDrawer={setDiscussionForumDrawer}
          isRecordedGoal={isRecordedGoal}
          toggleEnroll={toggleEnroll}
          setFreePlanUnlockDrawerState={setFreePlanUnlockDrawerState}
          setShowShareOptions={setShowShareOptions}
          showLoadingButton={showLoadingButton}
          setShowDialog={setShowDialog}
          openLiteUpgradeDialog={openLiteUpgradeDialog}
        />
        {isScheduled && (
          <AddedToPlannerCard
            show={showPlannerEditTooltip}
            hide={hidePlannerEditTooltip}
            dailyItemScheduleAt={plannerSchedule.daily_item_schedule_at}
            startDate={plannerSchedule.start_date}
            courseUid={courseUid}
            handleRemoveFromPlanner={handleRemoveFromPlanner}
            handleEditCoursePlan={handleEditCoursePlan}
            goalUid={goalUid}
            plusCourse={plusCourse}
            courseType={courseType}
          />
        )}
        <FreeTrial
          goalUID={plusCourse?.goal?.uid}
          freeTrialData={freeTrialData}
          onSubscribe={() => {
            subscribedNow.current = true;
          }}
          lps="Course Page"
        />
        <ActiveSubscriptionModal
          open={showSubscriptionNudge}
          goalUID={plusCourse?.goal?.uid}
        />
        <FreePlanModal
          open={showFreeTrialNudge}
          goalUID={plusCourse?.goal?.uid}
        />
      </Header>
      <FreePlanUnlock
        drawerState={freePlanUnlockDrawerState}
        closeDrawerHandler={closeFreePlanUnlockDrawer}
        objectType={unlockFreePlanFor.COURSE}
        objectUid={uid}
        callBack={() => {
          setTimeout(() => setFreePlanUnlockDrawerState(false), 3000);
          toggleEnroll(uid, isEnrolled, plusCourse);
        }}
      />
      {!isTestSeries &&
        !isEducatorSide &&
        !isRecordedGoal &&
        !hidePlannerAndUpdates &&
        !isHybridNonOfflineCourse && (
          <AddCourseToPlannerCard
            isEnrolled={isEnrolled}
            handleCreateCoursePlan={handleCreateCoursePlan}
            isScheduled={isScheduled}
            isCompleted={isCompleted}
          />
        )}
    </Wrapper>
  );
};

export default PlusCoursePageHeader;

PlusCoursePageHeader.propTypes = {
  toggleEnroll: PropTypes.func,
  handleRemoveFromPlanner: PropTypes.func.isRequired,
  setDiscussionForumDrawer: PropTypes.func.isRequired,
  lessonList: PropTypes.shape({}).isRequired,
  isTestSeries: PropTypes.bool,
  dataAnalytics: PropTypes.string
};

PlusCoursePageHeader.defaultProps = {
  toggleEnroll: () => {},
  isTestSeries: false,
  dataAnalytics: ''
};
